import { useFeatureIsOn } from '@growthbook/growthbook-react'
import {
  ProductTypeEnum,
  StoreImage,
  StoreImageEnum,
  StoreItemEnum,
} from '@systemeio/digital-store-shared'
import { Modal, ModalSizeEnum, PrimaryButton } from '@systemeio/ui'
import { GrowthbookFlagsEnum } from 'growthbook/types'
import {
  storeItemDescriptionLocoKeys,
  storeItemProductDescriptionLocoKeys,
  storeItemProductTitleLocoKeys,
  storeItemTitleLocoKeys,
} from 'modules/stores/store/constants/store-item-constants'
import { StoreTabEnum } from 'modules/stores/store/enum/store-tab-enum'
import { useStoreQuery } from 'modules/stores/store/hooks/use-store-query'
import { StoreItemWithTabType } from 'modules/stores/store/types/store-item-interface'
import { getImageTypeFromItem } from 'modules/stores/store/utils/get-image-type-from-item'
import { getTabFromItem } from 'modules/stores/store/utils/get-tab-from-item'
import { FC, useState } from 'react'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { AddIcon } from '@systemeio/ui'

const getItemCardProps = (
  itemType: StoreItemWithTabType,
  onCreateItem: (tab: StoreTabEnum, productType?: ProductTypeEnum) => Promise<boolean>,
  physicalProductIsOn: boolean,
): Props[] => {
  switch (itemType) {
    case StoreItemEnum.product: {
      const productTypes = Object.values(ProductTypeEnum)
      const filteredProductTypes = physicalProductIsOn
        ? productTypes
        : productTypes.filter(productType => productType !== ProductTypeEnum.physicalProduct)

      return filteredProductTypes.map(productType => {
        const imageType = getImageTypeFromItem({ itemType, productType })
        const title = storeItemProductTitleLocoKeys[productType]
        const description = storeItemProductDescriptionLocoKeys[productType]
        return {
          title,
          description,
          imageType,
          onCreate: () => onCreateItem(getTabFromItem(itemType), productType),
          key: productType,
        }
      })
    }
    default: {
      return [
        {
          title: storeItemTitleLocoKeys[itemType],
          description: storeItemDescriptionLocoKeys[itemType],
          imageType: getImageTypeFromItem({ itemType }),
          onCreate: () => onCreateItem(getTabFromItem(itemType)),
          key: itemType,
        },
      ]
    }
  }
}

interface Props {
  imageType: StoreImageEnum
  title: string
  description: string
  onCreate: () => Promise<boolean>
  key: string
}

const ItemCard: FC<Props> = ({ title, description, imageType, onCreate }) => {
  const { t } = useLocoTranslation()

  return (
    <button
      className={
        'border-text-secondary/30 hover:border-brand-primary main-transition-colors focus-visible:border-brand-primary text-start flex flex-col gap-3 rounded-lg border p-4 outline-hidden sm:p-6 md:p-8'
      }
      onClick={onCreate}
    >
      <StoreImage imageType={imageType} alt={t(title)} skipTheme />
      <b className={'text-text-primary'}>{t(title)}</b>
      <p>{t(description)}</p>
    </button>
  )
}

const AddItemModal: FC = () => {
  const { t } = useLocoTranslation()

  const { onCreateItem } = useStoreQuery()

  const [opened, setOpened] = useState(false)

  const physicalProductFeatureIsOn = useFeatureIsOn(GrowthbookFlagsEnum.DSPhysicalProduct)

  return (
    <>
      <PrimaryButton onClick={() => setOpened(true)}>
        <AddIcon />
        <span>{t('dashboard.store.add_product')}</span>
      </PrimaryButton>
      <Modal
        size={ModalSizeEnum.medium}
        opened={opened}
        onClose={() => setOpened(false)}
        title={t('dashboard.store.add_product')}
      >
        <div className={'grid grid-cols-2 gap-4 md:gap-5'}>
          {(
            Object.values(StoreItemEnum).filter(
              itemType => itemType !== StoreItemEnum.header,
            ) as StoreItemWithTabType[]
          )
            .map(itemType => getItemCardProps(itemType, onCreateItem, physicalProductFeatureIsOn))
            .flat()
            .map(itemProps => {
              const { key, ...rest } = itemProps
              return <ItemCard key={key} {...rest} />
            })}
        </div>
      </Modal>
    </>
  )
}

export default AddItemModal
